
import { BaseLocationFragment, CalendarEventLocation, CalendarEvent_LocationsFragment, FullJobFragment, LocationCreateBase } from '../../generated/graphql.generated';

import { CANADA, EventLocationTypes, USA } from '../global.constants';
import { getJobLocation } from '../jobs/jobs.util';

export function getLocationAreaCode(location: BaseLocationFragment | LocationCreateBase): string {
    if (!location || !location.areaCode || !location.country) { return 'Not Set'; }

    if (location.country === CANADA) {
        const unifiedAreaCode = removeWhiteSpaceFromAreaCode(location.areaCode);
        return `${unifiedAreaCode.substring(0, 3)} ${unifiedAreaCode.substring(3, 6)}`;
    } else if (location.country === USA) {
        return location.areaCode;
    }
}

//when lead comes from OBE, area code field has format "XXX XXX"
//when user fills locations from UI, area code field has format "XXXXXX"
//here we unify format of area code for proper work of getLocationAreaCode
export function removeWhiteSpaceFromAreaCode(areaCode: string): string {
    return areaCode.replace(/\s+/g, '');
}

export function getFirstLocation(
    ce: CalendarEvent_LocationsFragment,
    firstLocationType: EventLocationTypes = EventLocationTypes.start,
    hardMatch = false,
) {
    const locations = ce?.locations || [];
    let loc = hardMatch ? undefined : locations[0];
    for (const ceLocation of locations) {
        if (ceLocation?.type === firstLocationType) {
            loc = ceLocation;
            break;
        }
    }

    return loc;

}

export function getActualTravelTime(location: CalendarEventLocation): number{
    if (!location) { return 0; }
    return location.travelTimeToNextLocation + location.travelTimeToNextLocationOffset;
}

export function viewLocationOnGoogleMaps(location: BaseLocationFragment) {
    const url = `https://www.google.com/maps/place/${encodeURIComponent(convertLocationToFullAddress(location))}`;
    return url;
  }

export function convertLocationToFullAddress(location: BaseLocationFragment): string {
    if (!location) { return; }

    const areaCode = getLocationAreaCode(location);

    if (location.addressLineOne === areaCode) {
        return `${areaCode} ${location.country ? location.country : ''}`;
    }

    return `${location.addressLineOne} ${location.city} ${areaCode}`;
}

export function showGoogleMapsRoute(job:FullJobFragment) {
    // TODO: Update this to work with multiple locations
    const start = convertLocationToFullAddress(getJobLocation(job, 'start'));
    const end = convertLocationToFullAddress(getJobLocation(job, 'end'));

    const url = `https://www.google.ca/maps/dir/${encodeURIComponent(start)}/${encodeURIComponent(end)}`;
    return url;
}

export function showGoogleMapsRouteV2(...addresses: string[]) {
  if (addresses.length < 2) {
    throw new Error("At least two addresses are required to build a route.");
  }

  const encoded = addresses.map(addr => encodeURIComponent(addr));
  return `https://www.google.ca/maps/dir/${encoded.join("/")}`;
}

export function extractPostalCodeFromAddressLineOne(address: string): string | null {
    const match = address.match(/(\b\d{5}\b|\b[A-Z]\d[A-Z][ ]?\d[A-Z]\d\b)(?=,?\s*\w+$)/i);
    return match ? match[0] : null;
}

export function isLocationInvalid(locationEditor: {
    selectedLocation?: unknown;
    address?: string;
    unitNumber?: string;
}): boolean {

    if (!locationEditor) {
        return false;
    }

    if (!locationEditor.address && !locationEditor.unitNumber) {
        return false;
    }

    return !Boolean(locationEditor.selectedLocation);
}


/**
 * Is `city` already one of the comma-separated parts of `addressLineOne`?
 *
 * The app stores the whole formatted address in `addressLineOne` while still populating `city`, so
 * appending one to the other prints it twice. See #94 in docs/crew-navigator.md.
 *
 * Compares whole segments rather than using `includes()`: a substring test treats city "York" as
 * already present in "123 New York Ave" and silently drops a real city.
 */
export function addressContainsCity(addressLineOne?: string | null, city?: string | null): boolean {
    if (!addressLineOne || !city) { return false; }

    const target = city.trim().toLowerCase();
    if (!target) { return false; }

    return addressLineOne
        .split(',')
        .some((segment) => segment.trim().toLowerCase() === target);
}

/** The address with the city appended only when it adds something. Empty string if there is neither. */
export function formatAddressWithCity(addressLineOne?: string | null, city?: string | null): string {
    const address = (addressLineOne || '').trim();
    const cityName = (city || '').trim();

    if (!address) { return cityName; }
    if (!cityName || addressContainsCity(address, cityName)) { return address; }

    return `${address}, ${cityName}`;
}
